import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import ReviewSubmissionCard from './components/ReviewSubmissionCard';
import ReceivedReviewCard from './components/ReceivedReviewCard';
import RatingAnalytics from './components/RatingAnalytics';
import { reviewService } from '../../services/reviewService';
import DashboardSkeleton from '../../components/ui/DashboardSkeleton';
import ErrorFallback from '../../components/ui/ErrorFallback';
import toastService from '../../components/ui/Toast';

const ReviewsManagement = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [receivedReviews, setReceivedReviews] = useState([]);
  const [filterRating, setFilterRating] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [activeTab, setActiveTab] = useState('pending');
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user && userProfile) {
      loadReviews();
    }
  }, [user, userProfile]);

  const loadReviews = async () => {
    try {
      setDataLoading(true);
      setError(null);

      // Fetch completed bookings pending review
      const completedBookings = await reviewService?.getCompletedBookingsForReview(
        user?.id,
        userProfile?.role
      );

      // Transform bookings to pending reviews format
      const pending = completedBookings?.map(booking => ({
        id: booking?.id,
        bookingId: booking?.bookingReference,
        type: userProfile?.role === 'owner' ? 'renter' : 'trailer',
        subjectName: userProfile?.role === 'owner' 
          ? booking?.renter?.fullName 
          : booking?.trailer?.title,
        subjectImage: userProfile?.role === 'owner' ? booking?.renter?.profilePhotoUrl ||'https://img.rocket.new/generatedImages/rocket_gen_img_13ef7a567-1769876899260.png'
          : booking?.trailer?.photos?.[0] || 'https://img.rocket.new/generatedImages/rocket_gen_img_1b2a7fed7-1767694944518.png',
        subjectImageAlt: userProfile?.role === 'owner' 
          ? `${booking?.renter?.fullName} profile` 
          : `${booking?.trailer?.title}`,
        ownerName: booking?.owner?.fullName,
        rentalDates: `${new Date(booking?.startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(booking?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
        completedDate: booking?.endDate,
        daysAgo: Math?.floor((new Date() - new Date(booking?.endDate)) / (1000 * 60 * 60 * 24)),
        reviewedUserId: userProfile?.role === 'owner' ? booking?.renterId : booking?.ownerId,
        trailerListingId: booking?.trailerListingId
      }));

      setPendingReviews(pending || []);

      // Fetch received reviews
      const received = await reviewService?.getReceivedReviews(user?.id);

      // Transform received reviews
      const receivedFormatted = received?.map(review => ({
        id: review?.id,
        reviewerName: review?.reviewer?.fullName,
        reviewerType: review?.reviewer?.role === 'owner' ? 'owner' : 'renter',
        rating: review?.overallRating,
        categories: review?.categoryRatings || {
          communication: 0,
          condition: 0,
          cleanliness: 0,
          value: 0
        },
        comment: review?.comment,
        photos: review?.photos || [],
        bookingId: review?.booking?.bookingReference,
        trailerName: review?.trailer?.title,
        rentalDates: `${new Date(review?.booking?.startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(review?.booking?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
        reviewDate: review?.createdAt,
        hasResponse: false,
        helpful: 0
      }));

      setReceivedReviews(receivedFormatted || []);

      // Setup real-time subscription
      const channel = reviewService?.subscribeToReviews(user?.id, (payload) => {
        if (payload?.eventType === 'INSERT') {
          loadReviews();
        }
      });

      return () => {
        if (channel) {
          reviewService?.unsubscribeFromReviews(channel);
        }
      };
    } catch (err) {
      console.error('Error loading reviews:', err);
      setError(err?.message || 'Failed to load reviews');
    } finally {
      setDataLoading(false);
    }
  };

  const handleReviewSubmit = async (bookingId, reviewData) => {
    try {
      const booking = pendingReviews?.find(r => r?.id === bookingId);
      if (!booking) return;

      await reviewService?.submitReview({
        bookingId: booking?.id,
        reviewedUserId: booking?.reviewedUserId,
        trailerListingId: booking?.trailerListingId,
        reviewType: userProfile?.role === 'owner' ? 'owner_to_renter' : 'renter_to_trailer',
        overallRating: reviewData?.overallRating,
        categoryRatings: reviewData?.categoryRatings,
        comment: reviewData?.comment,
        photos: reviewData?.photos || []
      });

      // Remove from pending reviews
      setPendingReviews(pendingReviews?.filter(r => r?.id !== bookingId));
      
      // Show success toast
      toastService?.success('Review submitted successfully!');
    } catch (err) {
      console.error('Error submitting review:', err);
      toastService?.error('Failed to submit review. Please try again.');
    }
  };

  const handleResponseSubmit = (reviewId, response) => {
    console.log('Response submitted:', reviewId, response);
    setReceivedReviews(receivedReviews?.map(r =>
      r?.id === reviewId
        ? { ...r, hasResponse: true, response, responseDate: new Date()?.toISOString() }
        : r
    ));
  };

  const filteredReceivedReviews = receivedReviews?.filter(review => {
    if (filterRating && review?.rating !== parseInt(filterRating)) return false;
    return true;
  })?.sort((a, b) => {
    if (sortBy === 'recent') {
      return new Date(b?.reviewDate) - new Date(a?.reviewDate);
    } else if (sortBy === 'rating-high') {
      return b?.rating - a?.rating;
    } else if (sortBy === 'rating-low') {
      return a?.rating - b?.rating;
    }
    return 0;
  });

  const averageRating = receivedReviews?.length > 0
    ? (receivedReviews?.reduce((sum, r) => sum + r?.rating, 0) / receivedReviews?.length)?.toFixed(1)
    : 0;

  const ratingDistribution = [5, 4, 3, 2, 1]?.map(rating => ({
    rating,
    count: receivedReviews?.filter(r => r?.rating === rating)?.length,
    percentage: receivedReviews?.length > 0
      ? (receivedReviews?.filter(r => r?.rating === rating)?.length / receivedReviews?.length) * 100
      : 0
  }));

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <DashboardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <ErrorFallback error={error} onRetry={loadReviews} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Reviews & Ratings
            </h1>
            <p className="font-body text-muted-foreground">
              Manage your reviews and build trust in the community
            </p>
          </div>

          {/* Analytics Section */}
          <RatingAnalytics
            averageRating={averageRating}
            totalReviews={receivedReviews?.length}
            ratingDistribution={ratingDistribution}
            responseRate={85}
          />

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-border">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`pb-4 font-body font-medium transition-smooth relative ${
                    activeTab === 'pending' ?'text-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Pending Reviews
                  {pendingReviews?.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-primary text-primary-foreground rounded-full text-xs">
                      {pendingReviews?.length}
                    </span>
                  )}
                  {activeTab === 'pending' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('received')}
                  className={`pb-4 font-body font-medium transition-smooth relative ${
                    activeTab === 'received' ?'text-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Received Reviews
                  {activeTab === 'received' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Pending Reviews Tab */}
          {activeTab === 'pending' && (
            <div>
              {pendingReviews?.length > 0 ? (
                <div className="space-y-4">
                  {pendingReviews?.map((review) => (
                    <ReviewSubmissionCard
                      key={review?.id}
                      review={review}
                      userRole={userProfile?.role}
                      onSubmit={handleReviewSubmit}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg shadow-elevation-2 p-12 text-center">
                  <Icon name="CheckCircle" size={64} className="text-success mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                    All Caught Up!
                  </h3>
                  <p className="font-body text-muted-foreground">
                    You don't have any pending reviews at the moment.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Received Reviews Tab */}
          {activeTab === 'received' && (
            <div>
              {/* Filters */}
              <div className="mb-6 flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Select
                    id="filter-rating"
                    name="filterRating"
                    placeholder="Filter by rating"
                    value={filterRating}
                    onChange={setFilterRating}
                    options={[
                      { value: '', label: 'All Ratings' },
                      { value: '5', label: '5 Stars' },
                      { value: '4', label: '4 Stars' },
                      { value: '3', label: '3 Stars' },
                      { value: '2', label: '2 Stars' },
                      { value: '1', label: '1 Star' }
                    ]}
                    description=""
                    error=""
                  />
                </div>
                <div className="flex-1">
                  <Select
                    id="sort-by"
                    name="sortBy"
                    placeholder="Sort by"
                    value={sortBy}
                    onChange={setSortBy}
                    options={[
                      { value: 'recent', label: 'Most Recent' },
                      { value: 'rating-high', label: 'Highest Rating' },
                      { value: 'rating-low', label: 'Lowest Rating' }
                    ]}
                    description=""
                    error=""
                  />
                </div>
              </div>

              {/* Reviews List */}
              {filteredReceivedReviews?.length > 0 ? (
                <div className="space-y-4">
                  {filteredReceivedReviews?.map((review) => (
                    <ReceivedReviewCard
                      key={review?.id}
                      review={review}
                      userRole={userProfile?.role}
                      onResponseSubmit={handleResponseSubmit}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg shadow-elevation-2 p-12 text-center">
                  <Icon name="Star" size={64} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                    No Reviews Yet
                  </h3>
                  <p className="font-body text-muted-foreground">
                    {filterRating ? 'No reviews match your filter criteria.' : 'You haven\'t received any reviews yet.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewsManagement;